/**
 * DrillDownManager
 *
 * Builds drill-down queries dynamically from Cube.js annotation drillMembers.
 * No hard-coded measure names or query builders — works with any view/measure.
 *
 * Flow:
 *   User clicks value in measure column (e.g. total_instances = 1,234)
 *   → annotationMap[column].drillMembers = ["cataloging_view.title", ...]
 *   → query = { dimensions: drillMembers, filters: [currentDimension = rowValue] }
 *   → DrillDownModal shows results
 */

const DrillDownManager = {
  /**
   * Check if a measure column supports drill-down.
   * A measure supports drill-down if it has drillMembers in the annotation.
   *
   * @param {string} columnKey    - Column key with underscores e.g. "cataloging_view_total_instances"
   * @param {object} annotationMap - Built by buildAnnotationMap()
   * @returns {boolean}
   */
  supportsDrillDown: (columnKey, annotationMap = {}) => {
    const entry = annotationMap[columnKey];
    return !!(entry?.drillMembers?.length > 0);
  },

  /**
   * Build drill-down query from annotation drillMembers + current row context.
   *
   * @param {string} columnKey      - Measure column key e.g. "cataloging_view_total_instances"
   * @param {object} item           - Full row data from MultiColumnList
   * @param {object} annotationMap  - Built by buildAnnotationMap()
   * @param {Array}  dimensions     - Active dimensions in the current query (for filter context)
   * @returns {object|null}         - { query, measureName, measureValue, drillMembers }
   */
  buildDrillDownQuery: (
    columnKey,
    item,
    annotationMap = {},
    dimensions = [],
  ) => {
    const entry = annotationMap[columnKey];
    if (!entry?.drillMembers?.length) return null;

    const measureValue = item[columnKey];
    if (!measureValue || measureValue === '0') return null;

    // Build filters from active dimensions in the row
    const filters = dimensions
      .map((dim) => {
        const dimKey = dim.replace('.', '_');
        const value = item[dimKey];
        console.log(`[DrillDown] dim="${dim}" dimKey="${dimKey}" value="${value}"`);
        if (!value) return null;
        return { member: dim, operator: 'equals', values: [String(value)] };
      })
      .filter(Boolean);

    const query = {
      dimensions: entry.drillMembers,
      filters,
      limit: 50,
      order: entry.drillMembers[2] ? { [entry.drillMembers[2]]: 'desc' } : {},
    };

    console.group('[DrillDown] buildDrillDownQuery');
    console.log('columnKey:', columnKey);
    console.log('measureValue:', measureValue);
    console.log('activeDimensions:', dimensions);
    console.log('filters:', JSON.stringify(filters));
    console.log('drillMembers:', entry.drillMembers);
    console.log('item keys:', Object.keys(item));
    console.groupEnd();

    return {
      query,
      measureName: columnKey,
      measureValue,
      drillMembers: entry.drillMembers,
    };
  },
};

export default DrillDownManager;
